import type { ColTypeDef } from 'ag-grid-community';
import { getStatusFieldColor } from 'components/shared/Blotter/BlotterPanel/Fields/StatusField/StatusField';
import { cellTranslatorRenderer } from 'components/shared/Blotter/Grid/CellRenderers/cellTranslatorFactory';
import { useServerSideQuoteModeColumnType } from 'components/shared/Blotter/Grid/Hooks/useQuoteModeColumn';
import { useServerSideDefaultColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useServerSideDefaultColumnTypes';
import { useServerSideStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useStatusColumnType';
import { useServerSideTradeAuthorizationStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useTradeAuthorizationStatusColumnType';
import { useMemo } from 'react';

interface UseBlotterColumnTypesProps<TItem> {
  additionalColumns?: Record<string, ColTypeDef<TItem>>;
  precision?: number;
}

export function useBlotterColumnTypes<TItem>({
  additionalColumns,
  precision = 6,
}: UseBlotterColumnTypesProps<TItem> = {}) {
  const quoteModeColumn = useServerSideQuoteModeColumnType<TItem>();
  const statusColumn = useServerSideStatusColumnType<TItem>(getStatusFieldColor);
  const tradeAuthorizationColumn = useServerSideTradeAuthorizationStatusColumnType<TItem>();

  const customColumns = useMemo<Record<string, ColTypeDef<TItem>>>(
    () => ({
      quoteMode: quoteModeColumn,
      status: statusColumn,
      tradeAuthorizationStatus: tradeAuthorizationColumn,
      stringToTranslate: {
        cellRenderer: cellTranslatorRenderer(),
      },
      ...(additionalColumns ?? {}),
    }),
    [quoteModeColumn, statusColumn, tradeAuthorizationColumn, additionalColumns],
  );

  return useServerSideDefaultColumnTypes(customColumns, precision);
}
